import { z } from 'zod';

export const companyDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  tradeName: z.string().max(255).nullable(),
  legalForm: z.string().max(100).nullable(),
  registrationNumber: z.string().max(100).nullable(),
  taxId: z.string().max(100).nullable(),
  countryCode: z.string().length(2).nullable(),
  currencyCode: z.string().length(3).nullable(),
  languageCode: z.string().max(5).nullable(),
  timeZone: z.string().max(100).nullable(),
  addressLine1: z.string().max(255).nullable(),
  addressLine2: z.string().max(255).nullable(),
  postalCode: z.string().max(20).nullable(),
  city: z.string().max(255).nullable(),
  country: z.string().max(255).nullable(),
  phone: z.string().max(50).nullable(),
  email: z.string().max(255).email().nullable(),
  website: z.string().max(500).url().nullable(),
  logoUrl: z.string().max(500).url().nullable(),
  industry: z.string().max(100).nullable(),
  numberOfEmployees: z.number().int().nonnegative().nullable(),
  foundingYear: z.number().int().min(1800).max(2100).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const optionalString = (max: number) =>
  z
    .string()
    .max(max, { message: `Ce champ ne doit pas dépasser ${max} caractères` })
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v));

/** Vide HTML (`NaN`) → `null` en JSON ; Zod `.optional()` n’accepte pas `null`. */
const preprocessOptionalInt = (val: unknown): unknown => {
  if (val === null || val === undefined || val === '') return undefined;
  if (typeof val === 'number' && Number.isNaN(val)) return undefined;
  return val;
};

export const companyCreateSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'La raison sociale est obligatoire' })
    .max(255, { message: 'La raison sociale ne doit pas dépasser 255 caractères' }),
  tradeName: optionalString(255),
  legalForm: optionalString(100),
  registrationNumber: optionalString(100),
  taxId: optionalString(100),
  countryCode: z
    .union([
      z.literal(''),
      z.string().length(2, {
        message: 'Le code pays doit comporter 2 lettres (ex. FR)',
      }),
    ])
    .transform((v) => (v === '' ? undefined : v)),
  currencyCode: z
    .union([
      z.literal(''),
      z.string().length(3, {
        message: 'Le code devise doit comporter 3 lettres (ex. EUR)',
      }),
    ])
    .transform((v) => (v === '' ? undefined : v)),
  languageCode: z
    .string()
    .max(5, { message: 'Le code langue ne doit pas dépasser 5 caractères' })
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  timeZone: z
    .string()
    .max(100, { message: 'Le fuseau ne doit pas dépasser 100 caractères' })
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  addressLine1: optionalString(255),
  addressLine2: optionalString(255),
  postalCode: optionalString(20),
  city: optionalString(255),
  country: optionalString(255),
  phone: optionalString(50),
  email: z
    .union([
      z.undefined(),
      z.literal(''),
      z
        .string()
        .email({ message: 'Adresse e-mail invalide' })
        .max(255, { message: 'L’e-mail ne doit pas dépasser 255 caractères' }),
    ])
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  website: z
    .union([
      z.undefined(),
      z.literal(''),
      z
        .string()
        .max(500, { message: 'L’URL ne doit pas dépasser 500 caractères' })
        .url({ message: 'URL invalide' }),
    ])
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  logoUrl: z
    .union([
      z.undefined(),
      z.literal(''),
      z
        .string()
        .max(500, { message: 'L’URL ne doit pas dépasser 500 caractères' })
        .url({ message: 'URL invalide' }),
    ])
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  industry: optionalString(100),
  numberOfEmployees: z.preprocess(
    preprocessOptionalInt,
    z
      .number()
      .int({ message: 'Le nombre doit être un entier' })
      .nonnegative({ message: 'La valeur doit être positive ou nulle' })
      .optional(),
  ),
  foundingYear: z.preprocess(
    preprocessOptionalInt,
    z
      .number()
      .int({ message: 'L’année doit être un nombre entier' })
      .min(1800, { message: 'L’année est trop ancienne' })
      .max(2100, { message: 'L’année est trop éloignée' })
      .optional(),
  ),
});

export const companyUpdateSchema = companyCreateSchema.partial();

export type CompanyDto = z.infer<typeof companyDtoSchema>;
export type CompanyCreateInput = z.infer<typeof companyCreateSchema>;
export type CompanyUpdateInput = z.infer<typeof companyUpdateSchema>;
